import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import StatusCard from 'src/components/UI/addons/StatusCard';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Full-page confirmation shown after the FHQ / a custom form is submitted
// (Concept 1). Renders through the shared StatusCard (success variant) so it
// stays visually locked to the invalid-link page; the reference + submitted-time
// detail panel is this screen's extra content. Self-contained via props so it
// works both in-shell (fresh submit) and standalone (reload → server carries the
// reference/timestamp). `noun` serves both the FHQ ("questionnaire") and custom
// forms ("form"). Save-a-copy is Option A for now: a reassurance line, no button.
function DetailRow({ label, value, mono }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'baseline' },
        py: 1
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 600,
          textAlign: { xs: 'left', sm: 'right' },
          wordBreak: 'break-word',
          ...(mono && {
            fontFamily: 'monospace',
            fontSize: '1.05rem',
            letterSpacing: 1
          })
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function SubmissionConfirmation({
  reference,
  submittedAt,
  noun = 'questionnaire',
  // Closed by a negative stage 1 screen (TGCLOUD-1910), not by a submitted
  // questionnaire. It never says the patient is "not at risk": only that the
  // full questionnaire is not needed now, and when to get in touch.
  screenedOut = false
}) {
  const t = useAutoTranslation();
  const nounCap = noun.charAt(0).toUpperCase() + noun.slice(1);

  const formattedDate = submittedAt
    ? new Date(submittedAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    : null;

  return (
    <StatusCard
      variant="success"
      heading={
        screenedOut ? t('Thank you') : t('{{nounCap}} submitted', { nounCap })
      }
      subtext={
        screenedOut
          ? t(
              'Your answers have been sent securely to your healthcare provider.'
            )
          : t('Sent securely to your healthcare provider.')
      }
      note={
        screenedOut ? (
          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t(
                'Based on your answers, you do not need to fill in the full family history questionnaire at this time. This link is now closed.'
              )}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 1.5 }}
            >
              {t(
                'If your family history changes, or you are worried about cancer in your family, contact your healthcare provider.'
              )}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t(
                'Your healthcare provider will review your answers and be in touch if they need anything further. This {{noun}} link is now closed.',
                { noun }
              )}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 1.5, fontStyle: 'italic' }}
            >
              {t(
                'If you saved a PDF copy before submitting, keep it somewhere safe.'
              )}
            </Typography>
          </Box>
        )
      }
    >
      {(reference || formattedDate) && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: 2,
            border: '1px solid var(--color-border)',
            borderRadius: 2,
            bgcolor: 'var(--color-surface)',
            textAlign: 'left'
          }}
        >
          {reference && (
            <DetailRow label={t('Reference')} value={reference} mono />
          )}
          {reference && formattedDate && <Divider />}
          {formattedDate && (
            <DetailRow label={t('Submitted')} value={formattedDate} />
          )}
        </Paper>
      )}
    </StatusCard>
  );
}
